import { useState } from 'react';

interface HeatmapGoal {
  id: number;
  target_value: number | null;
  unit: string | null;
  title: string;
  goal_type: string;
}

interface HeatmapProps {
  heatmapData: Record<string, Record<number, number>>;
  goals: Array<HeatmapGoal>;
}

interface DayDetail {
  date: string;
  goals: Array<{
    goal: HeatmapGoal;
    value: number;
    target: number;
    met: boolean;
  }>;
}

export default function Heatmap({ heatmapData, goals }: HeatmapProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Generate last 14 days (2 weeks)
  const days: Date[] = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d);
  }

  const getIntensityClass = (value: number, target: number) => {
    if (value === 0) return 'bg-slate-700';

    const ratio = target > 0 ? value / target : value > 0 ? 1 : 0;

    if (ratio < 0.25) return 'bg-red-700';
    if (ratio < 0.5) return 'bg-orange-600';
    if (ratio < 0.8) return 'bg-amber-500';
    if (ratio < 1) return 'bg-emerald-500';
    return 'bg-emerald-700';
  };

  const getDayLabel = (d: Date) => {
    const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    return days[d.getDay()];
  };

  const getDayNumber = (d: Date) => d.getDate();

  const getCellData = (date: Date, goalId: number) => {
    const dateStr = date.toISOString().split('T')[0];
    return heatmapData[dateStr]?.[goalId] || 0;
  };

  const todayStr = new Date().toISOString().split('T')[0];

  const handleCellClick = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    setSelectedDate(dateStr);
  };

  const getDayDetail = (dateStr: string): DayDetail => {
    const dayGoals = goals.map((goal) => {
      const value = heatmapData[dateStr]?.[goal.id] || 0;
      const target = goal.target_value || 1;
      const ratio = goal.goal_type === 'checkbox'
        ? (value > 0 ? 1 : 0)
        : (target > 0 ? value / target : value > 0 ? 1 : 0);
      const met = goal.goal_type === 'checkbox' ? value > 0 : ratio >= 1;
      return { goal, value, target, met };
    });
    return { date: dateStr, goals: dayGoals };
  };

  const closeDetail = () => setSelectedDate(null);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  return (
    <>
      <div className="bg-slate-800 rounded-2xl p-3 border border-slate-700/50 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Activity Heatmap</h3>
          <span className="text-[10px] text-slate-500">14 days · tap for details</span>
        </div>

        {/* Scrollable container for the matrix */}
        <div className="overflow-x-auto -mx-1 px-1">
          <div className="min-w-[320px]">
            {/* Day header row */}
            <div className="flex items-end mb-1.5">
              {/* Goal name column header */}
              <div className="w-20 shrink-0 pr-2" />
              {/* Day columns */}
              <div className="flex-1 flex gap-[2px]">
                {days.map((day, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center min-w-0">
                    <span className="text-[8px] text-slate-500 font-medium">{getDayLabel(day)}</span>
                    <span className="text-[7px] text-slate-600">{getDayNumber(day)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Goal rows */}
            <div className="space-y-[3px]">
              {goals.map((goal) => {
                const target = goal.target_value || 1;
                return (
                  <div key={goal.id} className="flex items-center">
                    {/* Goal name */}
                    <div className="w-20 shrink-0 pr-2">
                      <span className="text-[9px] text-slate-400 truncate block leading-tight" title={goal.title}>
                        {goal.title}
                      </span>
                    </div>
                    {/* Day cells */}
                    <div className="flex-1 flex gap-[2px]">
                      {days.map((day, idx) => {
                        const value = getCellData(day, goal.id);
                        const intensityClass = getIntensityClass(value, target);
                        const isToday = day.toISOString().split('T')[0] === todayStr;
                        return (
                          <button
                            key={idx}
                            onClick={() => handleCellClick(day)}
                            className={`flex-1 aspect-square rounded-[2px] min-w-0 ${intensityClass} ${isToday ? 'ring-[0.5px] ring-white/40' : ''} transition-transform active:scale-90`}
                            title={`${goal.title}: ${value} on ${day.toLocaleDateString()}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-700/50">
          <div className="flex items-center gap-1.5">
            <span className="text-[8px] text-slate-500">—</span>
            <div className="flex gap-[2px]">
              <div className="w-2.5 h-2.5 rounded-[1px] bg-slate-700" />
              <div className="w-2.5 h-2.5 rounded-[1px] bg-red-700" />
              <div className="w-2.5 h-2.5 rounded-[1px] bg-orange-600" />
              <div className="w-2.5 h-2.5 rounded-[1px] bg-amber-500" />
              <div className="w-2.5 h-2.5 rounded-[1px] bg-emerald-500" />
              <div className="w-2.5 h-2.5 rounded-[1px] bg-emerald-700" />
            </div>
            <span className="text-[8px] text-slate-500">100%+</span>
          </div>
          <span className="text-[8px] text-slate-600">{goals.length} goals</span>
        </div>
      </div>

      {/* Detail Sheet */}
      {selectedDate && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={closeDetail}
          />
          {/* Sheet */}
          <div className="fixed bottom-0 left-0 right-0 bg-slate-800 rounded-t-2xl border-t border-slate-700/50 z-50 max-w-lg mx-auto max-h-[70vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-800 pt-3 pb-2 px-4 border-b border-slate-700/50">
              <div className="w-8 h-1 bg-slate-600 rounded-full mx-auto mb-3" />
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-base">{formatDate(selectedDate)}</h3>
                <button
                  onClick={closeDetail}
                  className="text-slate-400 hover:text-white text-lg px-2"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-4 space-y-3">
              {getDayDetail(selectedDate).goals.map(({ goal, value, target, met }) => (
                <div
                  key={goal.id}
                  className={`flex items-center justify-between p-3 rounded-xl border ${
                    met
                      ? 'bg-emerald-500/10 border-emerald-500/20'
                      : value > 0
                      ? 'bg-amber-500/10 border-amber-500/20'
                      : 'bg-slate-700/50 border-slate-700'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate">{goal.title}</h4>
                    <p className="text-[10px] text-slate-400">
                      {goal.goal_type === 'checkbox'
                        ? value > 0
                          ? '✓ Completed'
                          : '○ Not done'
                        : `${value} / ${target} ${goal.unit || ''}`}
                    </p>
                  </div>
                  <div className={`text-xs font-semibold px-2 py-1 rounded-lg ${
                    met
                      ? 'text-emerald-400 bg-emerald-500/20'
                      : value > 0
                      ? 'text-amber-400 bg-amber-500/20'
                      : 'text-slate-400 bg-slate-700'
                  }`}>
                    {met ? 'Met' : value > 0 ? 'Partial' : 'Missed'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
